"use client"

import { useEffect, useId, useMemo, useState } from "react"
import {
  STARTING_NODES,
  type LexicalText,
  type MilestoneOutcomeRequirement,
  type MilestoneOutcomeRequirementOutcome,
  type OutcomeRequiredErrorResponse,
} from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"

import { ApiRequestError } from "@/hooks/api/base"
import { AppDialogContent } from "@/components/dialogs/app-dialog-content"
import { markdownToNodes } from "@/components/lexical/serializer"
import { isFieldRequiredFor } from "@/components/projects/form-field-response-helpers"

import { OutcomeFormContent } from "./outcome-form-content"

interface OutcomeConfirmSelection {
  outcomeId: string
  projectComment?: LexicalText
  formResponses?: Record<string, string | number | boolean | null>
}

interface OutcomeRequirementDialogContentProps {
  requirement: MilestoneOutcomeRequirement
  onConfirm: (selection: OutcomeConfirmSelection) => Promise<void> | void
}

interface OpenOutcomeRequirementDialogOptions {
  requirement: MilestoneOutcomeRequirement
  onConfirm: (selection: OutcomeConfirmSelection) => Promise<void> | void
}

function outcomeDestinationName(outcome: MilestoneOutcomeRequirementOutcome): string {
  if (outcome.destination === "complete_project") {
    return "Project complete"
  }

  return outcome.target_project_milestone_title || "next milestone"
}

function outcomeDestinationDescription(outcome: MilestoneOutcomeRequirementOutcome): string {
  if (outcome.destination === "complete_project") {
    return "Complete project"
  }

  return `Move to ${outcomeDestinationName(outcome)}`
}

function textToLexicalContent(text: string): LexicalText {
  return {
    markdown: text,
    preferred_format: "markdown",
    nodes: markdownToNodes(text) || JSON.parse(JSON.stringify(STARTING_NODES)),
  }
}

function OutcomeRequirementDialogContent({ requirement, onConfirm }: OutcomeRequirementDialogContentProps) {
  const closeDialog = useGlobalDialogStore((state) => state.popDialog)
  const formId = useId()
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<string>(requirement.outcomes[0]?.id ?? "")
  const [commentText, setCommentText] = useState(requirement.outcomes[0]?.default_comment || "")
  const [formResponses, setFormResponses] = useState<Record<string, string | number | boolean | null>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const outcomeOptions = useMemo(
    () =>
      requirement.outcomes.map((outcome) => ({
        value: outcome.id,
        triggerLabel: outcome.label,
        label: (
          <span className="flex flex-col items-start">
            <span className="whitespace-normal">{outcome.label}</span>
            <span className="text-muted-foreground text-xs font-normal whitespace-normal">
              {outcomeDestinationDescription(outcome)}
            </span>
          </span>
        ),
      })),
    [requirement.outcomes]
  )

  const selectedOutcome = useMemo(
    () => requirement.outcomes.find((outcome) => outcome.id === selectedOutcomeId),
    [requirement.outcomes, selectedOutcomeId]
  )
  const selectedOutcomeDestination = useMemo(
    () => (selectedOutcome ? outcomeDestinationName(selectedOutcome) : "next milestone"),
    [selectedOutcome]
  )

  const selectedFormFields = useMemo(
    () => selectedOutcome?.destination_form_fields ?? [],
    [selectedOutcome?.destination_form_fields]
  )
  const hasFormFields = selectedFormFields.length > 0
  const formFieldsHeader = `Form fields for ${selectedOutcomeDestination}`

  useEffect(() => {
    setCommentText(selectedOutcome?.default_comment || "")
  }, [selectedOutcomeId, selectedOutcome?.default_comment])

  useEffect(() => {
    const fields = selectedOutcome?.destination_form_fields ?? []
    const initial: Record<string, string | number | boolean | null> = {}
    for (const field of fields) {
      if (field.current_value == null) continue
      if (typeof field.current_value === "object" && "storage_file_id" in field.current_value) {
        initial[field.id] = field.current_value.storage_file_id
      } else {
        initial[field.id] = field.current_value
      }
    }
    setFormResponses(initial)
  }, [selectedOutcomeId, selectedOutcome?.destination_form_fields])

  const isFormValid = useMemo(() => {
    if (!hasFormFields) return true
    return selectedFormFields.every((field) => {
      if (!isFieldRequiredFor(field, requirement.household_id ?? null)) return true
      const value = formResponses[field.id]
      return value != null && value !== ""
    })
  }, [hasFormFields, selectedFormFields, formResponses, requirement.household_id])

  const handleConfirm = async () => {
    if (!selectedOutcomeId || !selectedOutcome || isSubmitting || !isFormValid) return

    setIsSubmitting(true)

    try {
      const trimmedComment = commentText.trim()
      let projectComment: LexicalText | undefined
      if (trimmedComment) {
        const destination = outcomeDestinationName(selectedOutcome)
        const prefix = `**${requirement.milestone_title} → ${destination}**`
        projectComment = textToLexicalContent(`${prefix}\n\n${trimmedComment}`)
      }
      await onConfirm({
        outcomeId: selectedOutcomeId,
        projectComment,
        ...(hasFormFields && { formResponses }),
      })
      closeDialog()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AppDialogContent
      variant="gridForm"
      dismiss={isSubmitting ? "none" : "all"}
      title={"Outcome required"}
      action={{
        label: "Select outcome",
        form: formId,
        disabled: !selectedOutcomeId || !isFormValid,
        pending: isSubmitting,
      }}
      cancel={{ disabled: isSubmitting }}
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void handleConfirm()
        }}
      >
        <OutcomeFormContent
          description={
            <p className="text-muted-foreground text-sm">
              Choose what should happen after completing{" "}
              <span className="font-medium">{requirement.milestone_title}</span> in{" "}
              <span className="font-medium">{requirement.project_name}</span>.
            </p>
          }
          outcomeOptions={outcomeOptions}
          selectedOutcomeId={selectedOutcomeId}
          onOutcomeChange={setSelectedOutcomeId}
          commentText={commentText}
          onCommentChange={setCommentText}
          formFields={selectedFormFields.map((field) => ({
            ...field,
            current_file:
              field.field_type === "file_attachment" &&
              field.current_value &&
              typeof field.current_value === "object" &&
              "storage_file_id" in field.current_value
                ? field.current_value
                : null,
          }))}
          formFieldsHeader={formFieldsHeader}
          formResponses={formResponses}
          onFormResponsesChange={setFormResponses}
          disabled={isSubmitting}
          selectId={`outcome-required-select-${requirement.project_id}`}
          commentId={`outcome-required-comment-${requirement.project_id}`}
          householdRecordScope={requirement.household_id ? { householdId: requirement.household_id } : undefined}
          fileAttachmentScope={{
            householdId: requirement.household_id ?? null,
            projectTemplateId: requirement.project_template_id ?? null,
            hasStorageFolder: requirement.household_has_storage_folder ?? false,
          }}
        />
      </form>
    </AppDialogContent>
  )
}

export function openOutcomeRequirementDialog({ requirement, onConfirm }: OpenOutcomeRequirementDialogOptions) {
  useGlobalDialogStore.getState().openDialog({
    variant: "gridForm",
    content: <OutcomeRequirementDialogContent requirement={requirement} onConfirm={onConfirm} />,
  })
}

export function getOutcomeRequirementFromError(error: unknown): MilestoneOutcomeRequirement | null {
  if (!(error instanceof ApiRequestError) || error.status !== 422) {
    return null
  }

  const errorBody = error.responseBody as OutcomeRequiredErrorResponse | undefined
  if (!errorBody || errorBody.error_code !== "outcome_required") {
    return null
  }

  return errorBody.milestone_outcome_requirement || null
}
